import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import type { ReviewCycle, User } from './types';
import { GuideLink } from '../common';
import { detectRevisionType, formatMismatchMessage, workingMaterialReminder, startFromWorkingFileHint } from './revisionRules';
import CompanionDecisionModal, { CompanionDecision } from './CompanionDecisionModal';
import { ROLE_GUIDES } from '../../utils';
import {
  SubmitChooserModal,
  SubmitForReviewModal,
  SubmissionQueueModal,
  SubmitFileMetadataModal,
  SubmissionQueueFile
} from './ReviewDashboard';

/**
 * Phase 8B — Producer Dashboard.
 *
 * Five-tab view scoped to the current producer's cycles. Backend endpoint
 * (GET /api/content-review/producer-dashboard) returns lineage-grouped
 * entries with `latest` + `versions[]` per content thread.
 *
 * Tab semantics (from D4):
 *   - Action Required: producer = me AND status = revision_needed AND no
 *     successor cycle exists. Default tab when count > 0.
 *   - In Review: producer = me AND status IN [pending_prescreen,
 *     pending_review, in_review].
 *   - Approved: producer = me AND status = approved.
 *   - Rejected: producer = me AND status = rejected.
 *   - Submission Queue: LIVE since Phase J (the "deferred / 501" note here
 *     was stale). Serves the SharePoint Ready-to-Review folder minus files a
 *     cycle already claims. Falls back to an `unavailable` flag, not a crash,
 *     when there is no Graph token.
 *
 * onOpenCycle is wired by the parent so clicking a row opens the existing
 * ReviewInterface / DocumentViewer for that cycle.
 */

export type DashboardTab = 'action_required' | 'in_review' | 'approved' | 'rejected' | 'submission_queue';

export interface DashboardCounts {
  action_required: number;
  in_review: number;
  approved: number;
  rejected: number;
}

export interface DashboardLineageEntry {
  lineageKey: string;
  latest: ReviewCycle;
  versions: Array<{
    _id: string;
    version: string | null;
    status: string;
    createdAt: string;
  }>;
}

interface SubmissionQueueRow {
  fileId: string;
  fileName: string;
  size?: number;
  lastModified?: string;
  parsedMeta?: { purpose: string; audience: string; title: string; version: string } | null;
}

interface ProducerDashboardProps {
  /** Current user — needed for the submit-content modals. Optional so the
   *  unit tests can render without a full User stub. */
  user?: User | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onOpenCycle: (cycleId: string) => void;
  /** Optional: parent triggers a refresh by changing this key. */
  refreshKey?: number;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const TAB_DEFS: Array<{ key: DashboardTab; label: string; emoji?: string }> = [
  { key: 'action_required', label: 'Action Required', emoji: '⚠️' },
  { key: 'submission_queue', label: 'Submission Queue' },
  { key: 'in_review', label: 'In Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' }
];

const ProducerDashboard: React.FC<ProducerDashboardProps> = ({ user = null, getAuthHeaders, onOpenCycle, refreshKey = 0 }) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('action_required');
  const [counts, setCounts] = useState<DashboardCounts>({ action_required: 0, in_review: 0, approved: 0, rejected: 0 });
  const [cycles, setCycles] = useState<DashboardLineageEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLineage, setExpandedLineage] = useState<string | null>(null);
  // Submit-content flow state machine — mirrors ReviewDashboard's exactly,
  // reusing the same modal components. 'chooser' opens the file/URL picker;
  // 'web' opens the URL form; 'file-queue' opens the SharePoint picker;
  // {kind:'file-form', file} renders the metadata form for a picked file.
  type SubmitFlow = null | 'chooser' | 'web' | 'file-queue' | { kind: 'file-form'; file: SubmissionQueueFile };
  const [submitFlow, setSubmitFlow] = useState<SubmitFlow>(null);
  // Spec §11.4 / Phase 5 — "Submit revision": pick the fixed file from the
  // Ready folder and submit it AGAINST the revision-needed cycle. Lineage
  // dims are copied server-side; the producer never re-picks topic.
  const [revisionTarget, setRevisionTarget] = useState<{ _id: string; name: string; contentType: string | null; hasCompanion: boolean } | null>(null);
  // pdf-with-companion revision: the picked pdf waits for the companion
  // decision (COMPANION_DECISION_REQUIRED on the backend, 2026-09-01).
  const [pendingPdf, setPendingPdf] = useState<{ target: { _id: string; name: string; contentType: string | null }; file: SubmissionQueueFile } | null>(null);
  // "Submit update" on an APPROVED lineage head (path B, 2026-09-01): the
  // normal submit flow, prefilled with the lineage identity so the next
  // version cannot fork onto a new thread by a re-typed title.
  const [updatePrefill, setUpdatePrefill] = useState<Record<string, string> | null>(null);
  // Format policy lives in revisionRules.ts (one home, 2026-09-01 — shared
  // with the in-cycle SubmitRevisionControl).
  const [revisionBusy, setRevisionBusy] = useState(false);
  const [revisionMsg, setRevisionMsg] = useState<string | null>(null);
  const [revisionOk, setRevisionOk] = useState(false);
  // A web page has no file to pick (2026-08-24). The revision is a re-capture
  // of the same URL as the next version; the backend copies the lineage
  // verbatim from the source cycle, so the producer never re-picks topic or
  // audience and the successor cannot drift onto a different lineage key.
  const submitWebRevision = async () => {
    if (!revisionTarget) return;
    try {
      setRevisionBusy(true);
      const headers = await getAuthHeaders();
      const r = await axios.post(
        `${backendUrl}/api/content-review/cycles/${revisionTarget._id}/revisions`, {}, { headers }
      );
      const re = r.data?.reentry === 'dm' ? 'goes straight to the decision maker' : 're-enters review';
      setRevisionOk(true);
      setRevisionMsg(`v${r.data?.contentItem?.version || '?'} of "${revisionTarget.name}" ${re}. The page was re-captured from its URL.`);
      setRevisionTarget(null);
      setInternalRefreshKey(k => k + 1);
    } catch (err: any) {
      setRevisionOk(false);
      setRevisionMsg(err.response?.data?.error || 'Failed to submit the revision');
      setRevisionTarget(null);
    } finally {
      setRevisionBusy(false);
      setTimeout(() => document.getElementById('revision-msg')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    }
  };

  const submitRevision = async (file: SubmissionQueueFile) => {
    if (!revisionTarget) return;
    const picked = detectRevisionType(file.fileName);
    if (revisionTarget.contentType && picked !== revisionTarget.contentType) {
      setRevisionOk(false);
      setRevisionMsg(formatMismatchMessage(file.fileName, picked, revisionTarget.name, revisionTarget.contentType));
      setRevisionTarget(null);
      setTimeout(() => document.getElementById('revision-msg')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    if (revisionTarget.contentType === 'pdf' && revisionTarget.hasCompanion) {
      // The pdf's editable master must be handed over (or attested unchanged)
      // before the revision is accepted — same rule as the in-cycle control.
      setPendingPdf({ target: revisionTarget, file });
      setRevisionTarget(null);
      return;
    }
    await postRevision(revisionTarget, file, {});
  };

  const postRevision = async (target: { _id: string; name: string; contentType: string | null }, file: SubmissionQueueFile, extras: Record<string, unknown>) => {
    try {
      setRevisionBusy(true);
      const headers = await getAuthHeaders();
      const r = await axios.post(`${backendUrl}/api/content-review/cycles/${target._id}/revisions`, {
        fileId: file.fileId, originalFileName: file.fileName, ...extras
      }, { headers });
      const re = r.data?.reentry === 'dm' ? 'goes straight to the decision maker' : 're-enters review';
      setRevisionOk(true);
      setRevisionMsg(`v${r.data?.contentItem?.version || '?'} of "${target.name}" ${re}.${workingMaterialReminder(target.contentType)}`);
      setRevisionTarget(null);
      setInternalRefreshKey(k => k + 1);
    } catch (err: any) {
      // Keep the message where the producer is looking — the picker has just
      // closed, and a line at the bottom of a long list reads as "nothing happened".
      setRevisionOk(false);
      setRevisionMsg(err.response?.data?.error || 'Failed to submit revision');
    } finally {
      setRevisionBusy(false);
      setTimeout(() => document.getElementById('revision-msg')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    }
  };
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const [queueFiles, setQueueFiles] = useState<SubmissionQueueRow[]>([]);
  const [queueUnavailable, setQueueUnavailable] = useState<{ unavailable: boolean; reason?: string }>({ unavailable: false });

  const fetchTab = useCallback(async (tab: DashboardTab) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const url = `${backendUrl}/api/content-review/producer-dashboard${tab ? `?tab=${tab}` : ''}`;
      const res = await axios.get(url, { headers });
      // Submission queue branch returns counts:null + files[]; the regular
      // tabs return counts + cycles[]. Handle both shapes.
      if (res.data.activeTab === 'submission_queue') {
        setActiveTab('submission_queue');
        setQueueFiles(res.data.files || []);
        setQueueUnavailable({ unavailable: !!res.data.unavailable, reason: res.data.unavailableReason });
        // Don't clobber counts — keep the previously-loaded ones so the
        // sibling tabs' badges stay accurate.
      } else {
        setCounts(res.data.counts);
        setActiveTab(res.data.activeTab);
        setCycles(res.data.cycles || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
      setCycles([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Initial load — let backend pick the default active tab. Refetches when
  // either the parent refreshKey OR our internal refresh key (post-submit)
  // changes.
  useEffect(() => {
    fetchTab('action_required');   // hint preference; backend may fall through
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, internalRefreshKey]);

  const handleTabClick = (tab: DashboardTab) => {
    fetchTab(tab);
  };

  return (
    <div data-testid="producer-dashboard" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#123250' }}>My submissions <GuideLink href={ROLE_GUIDES.producer} label="Producer guide" /></h2>
        {/* Top CTA — submit content. Wired by the parent in a later commit. */}
        <button
          data-testid="submit-content-cta"
          style={{
            background: '#123250', color: 'white', border: 'none',
            padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14
          }}
          onClick={() => { setUpdatePrefill(null); setSubmitFlow('chooser'); }}
        >
          + Submit content
        </button>
      </div>

      <div role="tablist" aria-label="Producer dashboard tabs"
           style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: 16 }}>
        {TAB_DEFS.map(t => {
          const count = (counts as any)[t.key] as number | undefined;
          const isActive = activeTab === t.key;
          /* Submission Queue used to render greyed, with cursor:not-allowed and
             a "coming soon" tooltip, from `disabled = t.key === 'submission_queue'`.
             It was never actually disabled — the prop read
             `disabled && ... && false`, so it evaluated to false and the click
             handler ran. The tab looked broken and worked (Fredrik, 2026-08-28).

             The costume was the stale half. The backend stopped returning 501
             when Phase J wired /submission-queue into this endpoint; it serves
             the SharePoint Ready-to-Review folder minus already-claimed files,
             which is exactly what "+ Submit content" picks from. So the tab is
             live and now looks it. The genuine unavailable case — no Graph
             token, common for an external user — still arrives as the
             `unavailable` flag and renders its own explanatory panel below. */
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              data-testid={`tab-${t.key}`}
              onClick={() => handleTabClick(t.key)}
              style={{
                padding: '10px 16px',
                background: isActive ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid #123250' : '2px solid transparent',
                color: isActive ? '#123250' : '#666',
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14
              }}
            >
              {t.emoji ? `${t.emoji} ` : ''}{t.label}
              {typeof count === 'number' && count > 0 && (
                <span data-testid={`badge-${t.key}`}
                      style={{
                        marginLeft: 8,
                        background: t.key === 'action_required' ? '#dc3545' : '#666',
                        color: 'white',
                        borderRadius: 10,
                        padding: '2px 8px',
                        fontSize: 11
                      }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {loading && <p>Loading…</p>}
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {revisionMsg && (
        <div id="revision-msg" data-testid="revision-msg" role="alert"
             style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
                      background: revisionOk ? '#eaf7ee' : '#f8d7da', color: revisionOk ? '#155724' : '#842029',
                      border: `1px solid ${revisionOk ? '#b7e1c1' : '#f1aeb5'}` }}>
          <strong>{revisionOk ? 'Revision submitted' : 'Revision not submitted'}</strong> — {revisionMsg}
        </div>
      )}

      {!loading && activeTab === 'submission_queue' && queueUnavailable.unavailable && (
        <div data-testid="submission-queue-unavailable"
             style={{ padding: 24, background: '#fff8e1', borderRadius: 8, color: '#666', border: '1px solid #ffe082' }}>
          <strong>Submission Queue temporarily unavailable.</strong>
          <p style={{ margin: '6px 0 0', fontSize: 13 }}>
            SharePoint enumeration needs an internal Azure AD session — sign in with
            your @cellcolabs.com account to populate this list.
          </p>
          {queueUnavailable.reason && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#999' }}>Detail: {queueUnavailable.reason}</p>
          )}
        </div>
      )}

      {!loading && activeTab === 'submission_queue' && !queueUnavailable.unavailable && queueFiles.length === 0 && (
        <p data-testid="empty-state-queue" style={{ color: '#666' }}>
          No unclaimed files in the SharePoint <em>Ready to Review</em> folder.
        </p>
      )}

      {!loading && activeTab === 'submission_queue' && !queueUnavailable.unavailable && queueFiles.length > 0 && (
        <ul data-testid="submission-queue-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {queueFiles.map(file => (
            <li key={file.fileId}
                data-testid={`queue-file-${file.fileId}`}
                style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 8, cursor: 'pointer' }}
                onClick={() => setSubmitFlow({ kind: 'file-form', file })}>
              <div style={{ fontWeight: 600 }}>{file.fileName}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {file.parsedMeta
                  ? `${file.parsedMeta.purpose} · ${file.parsedMeta.audience} · ${file.parsedMeta.title} · v${file.parsedMeta.version}`
                  : 'Click to fill in metadata and submit'}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && activeTab !== 'submission_queue' && cycles.length === 0 && (
        <p data-testid="empty-state" style={{ color: '#666' }}>
          Nothing in this tab.
        </p>
      )}

      {revisionTarget && !revisionBusy && revisionTarget.contentType === 'webpage' && (
        <div className="review-modal-overlay" onClick={() => setRevisionTarget(null)}>
          <div className="review-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()} data-testid="web-revision-confirm">
            <h3 style={{ color: '#1a1a1a' }}>Submit revision</h3>
            <p style={{ fontSize: 14, color: '#444' }}>
              <strong>{revisionTarget.name}</strong> is a web page, so there is no file to pick.
              Once the fixes are live on the page, CellcoHub re-captures it as the next version —
              the reviewers see the updated page, not the one they commented on.
            </p>
            <p style={{ fontSize: 13, color: '#666' }}>
              Make sure the change list has been applied to the page first. Everything else about
              the review — audience, topic, who reviews it — carries over unchanged.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="review-btn review-btn-outline" onClick={() => setRevisionTarget(null)}>
                Cancel
              </button>
              <button className="review-btn review-btn-primary" onClick={submitWebRevision}>
                Re-capture and submit
              </button>
            </div>
          </div>
        </div>
      )}

      {revisionTarget && !revisionBusy && revisionTarget.contentType !== 'webpage' && (
        <SubmissionQueueModal
          getAuthHeaders={getAuthHeaders}
          onPick={submitRevision}
          onClose={() => setRevisionTarget(null)}
        />
      )}

      {/* Submit-content flow — chooser → file picker / URL form → metadata form */}
      {submitFlow === 'chooser' && (
        <SubmitChooserModal
          onPickFile={() => setSubmitFlow('file-queue')}
          onPickWebPage={() => setSubmitFlow('web')}
          onClose={() => { setSubmitFlow(null); setUpdatePrefill(null); }}
        />
      )}
      {submitFlow === 'web' && (
        <SubmitForReviewModal
          user={user}
          getAuthHeaders={getAuthHeaders}
          onSubmitted={() => { setSubmitFlow(null); setUpdatePrefill(null); setInternalRefreshKey(k => k + 1); }}
          onClose={() => { setSubmitFlow(null); setUpdatePrefill(null); }}
        />
      )}
      {submitFlow === 'file-queue' && (
        <SubmissionQueueModal
          getAuthHeaders={getAuthHeaders}
          onPick={(file) => setSubmitFlow({ kind: 'file-form', file })}
          onClose={() => { setSubmitFlow(null); setUpdatePrefill(null); }}
        />
      )}
      {pendingPdf && !revisionBusy && (
        <CompanionDecisionModal
          contentName={pendingPdf.target.name}
          getAuthHeaders={getAuthHeaders}
          onDecide={(d: CompanionDecision) => {
            const { target, file } = pendingPdf;
            setPendingPdf(null);
            postRevision(target, file, d.kind === 'new' ? { sourceFileId: d.fileId, sourceFileName: d.fileName } : { sourceUnchanged: true });
          }}
          onClose={() => setPendingPdf(null)}
        />
      )}
      {submitFlow && typeof submitFlow === 'object' && submitFlow.kind === 'file-form' && (
        <SubmitFileMetadataModal
          user={user}
          file={submitFlow.file}
          prefill={updatePrefill || undefined}
          getAuthHeaders={getAuthHeaders}
          onBack={() => setSubmitFlow('file-queue')}
          onSubmitted={() => { setSubmitFlow(null); setUpdatePrefill(null); setInternalRefreshKey(k => k + 1); }}
          onClose={() => { setSubmitFlow(null); setUpdatePrefill(null); }}
        />
      )}

      {!loading && activeTab !== 'submission_queue' && cycles.length > 0 && (
        <ul data-testid="cycle-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {cycles.map(entry => {
            const item = entry.latest.contentItem || ({} as any);
            const isExpanded = expandedLineage === entry.lineageKey;
            return (
              <li
                key={entry.lineageKey}
                data-testid={`cycle-row-${entry.latest._id}`}
                style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 8 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => onOpenCycle(entry.latest._id)}>
                    <div style={{ fontWeight: 600 }}>{item.name || 'Untitled'}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {item.type || '—'} · {item.audience || '—'} · v{item.version || '?'} · status {entry.latest.status}
                    </div>
                    {/* Q3 (2026-08-24) — a held cycle's status is plain
                        'approved', so the status line alone reads as finished.
                        It is the opposite: approved, and waiting on this
                        person. Say so on the row, not only inside the cycle. */}
                    {(entry.latest as any).pendingFixes && (
                      <div
                        data-testid={`pending-fixes-${entry.latest._id}`}
                        style={{ fontSize: 12, color: '#1e7e34', fontWeight: 600, marginTop: 2 }}
                      >
                        Approved — complete the fixes, then confirm to publish
                      </div>
                    )}
                  </div>
                  {entry.latest.status === 'revision_needed' && entry.latest.amendment?.appliedCopy?.webUrl && (
                    <a href={entry.latest.amendment.appliedCopy.webUrl} target="_blank" rel="noreferrer"
                       style={{ fontSize: 12, marginRight: 10, color: '#1e7e34' }}
                       title="CellcoHub applied the text fixes in the working file — open, review, finish the rest">
                      Working file ↗
                    </a>
                  )}
                  {entry.latest.status === 'revision_needed' && (
                    <button
                      data-testid={`submit-revision-${entry.latest._id}`}
                      onClick={() => { setRevisionMsg(null); setRevisionTarget({ _id: entry.latest._id, name: item.name || 'Untitled', contentType: entry.latest.contentType || null, hasCompanion: !!item.sourceFileId }); }}
                      style={{ background: '#123250', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 13, marginRight: 8 }}
                      title={entry.latest.contentType === 'webpage'
                        ? 'Re-capture the page as the next version of this cycle'
                        : `Pick the fixed file from Ready to Review and submit it as the next version of this cycle. ${startFromWorkingFileHint(entry.latest.contentType)}`.trim()}
                    >
                      Submit revision
                    </button>
                  )}
                  {entry.latest.status === 'approved' && (
                    <button
                      data-testid={`submit-update-${entry.latest._id}`}
                      onClick={() => {
                        setUpdatePrefill({
                          title: item.name || '', purpose: item.type || '', audience: item.audience || '',
                          topic: item.topic || '', geography: item.geography || 'global',
                          channel: entry.latest.channel || '', publicName: (item as any).publicName || ''
                        });
                        setSubmitFlow('file-queue');
                      }}
                      style={{ background: '#2E5477', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 13, marginRight: 8 }}
                      title={`Submit the next version of this approved content — identity fields come prefilled so the version history stays on one thread. ${startFromWorkingFileHint(entry.latest.contentType)}`.trim()}
                    >
                      Submit update
                    </button>
                  )}
                  {entry.versions.length > 1 && (
                    <button
                      data-testid={`expand-${entry.lineageKey}`}
                      onClick={() => setExpandedLineage(isExpanded ? null : entry.lineageKey)}
                      aria-expanded={isExpanded}
                      style={{
                        background: 'none',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: '#1a1a1a',
                        fontSize: 13,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <span style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms' }}>▸</span>
                      {isExpanded ? 'Hide' : `${entry.versions.length} versions`}
                    </button>
                  )}
                </div>
                {isExpanded && (
                  <ul data-testid={`versions-${entry.lineageKey}`}
                      style={{ marginTop: 8, paddingLeft: 16, fontSize: 13, color: '#666' }}>
                    {entry.versions.map(v => (
                      <li key={v._id} style={{ marginBottom: 2 }}>
                        v{v.version || '?'} — {v.status} ({new Date(v.createdAt).toLocaleDateString()})
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProducerDashboard;
